
'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Menu,
  X,
  Shield,
  Settings,
  Loader2,
  ChevronDown, // Added for dropdown indicator
  LogOut,
  LayoutDashboard,
  Search, // Import the Search icon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '@/components/ui/sheet';
import { NavLink } from '@/components/layout/NavLink';
import Image from 'next/image';
import {
  Avatar,
  AvatarFallback,
  AvatarImage
} from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu';
import { useUser } from '@/context/UserContext';
import { LogoutButton } from './LogoutButton';

// Define the types for our navigation links
interface NavLinkItem {
  href: string;
  label: string;
  adminOnly?: boolean; // New optional flag
  variant?: 'brandPink';
  children?: never; // This item should not have children
}

interface NavSubMenuItem {
  label: string;
  children: { href: string; label: string; }[];
  href?: never; // This item should not have a direct href
  variant?: 'brandPink';
}

type NavItem = NavLinkItem | NavSubMenuItem;

// Define navigation links with the new type
const publicNavLinks: NavItem[] = [
  { href: '/', label: 'Home' },
 /*  {
    label: 'About Zamzi',
    children: [
      { href: '/#features', label: 'Features' },
      { href: '/#why-zamzi', label: 'Why Zamzi Connect' },
      { href: '/#vetting', label: 'Zamzi Connect Verification' },
      { href: '/chat', label: 'AI Assistant' },
      { href: '/#testimonials', label: 'Testimonials' },
    ]
  }, */
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/search-profiles', label: 'Search' },
  { href: '/stays-discover', label: 'Zamzi Stay' },
  {
    label: 'Fix-It Squad',
    children: [
      { href: '/fix-it/request?urgency=immediate', label: 'Get Emergency Help' },
      { href: '/fix-it', label: 'Browse Fix-It Squad Pros' },
      { href: '/create-profile/fix-it-squad', label: 'Join Fix-It Squad' },
    ]
  },
    { href: '/craving', label: 'Craving' },
  { href: '/retail', label: 'Shopaholic' },
    { href: '/for-business-owners', label: 'For Business Owners' },
    {
      label: 'Extras',
      children: [
        { href: '/resources', label: 'All Resources' },
        { href: '/faq', label: 'FAQ' },
        { href: '/feedback', label: 'Feedback' },
        { href: '/contact', label: 'Contact' },
      ]
    },
];

const authenticatedNavLinks: NavItem[] = [
    { href: '/', label: 'Home' },
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/about', label: 'About' },
    { href: '/blog', label: 'Blog' },
    { href: '/search-profiles', label: 'Search' },
        { href: '/stays-discover', label: 'Zamzi Stay' },
    {
      label: 'Fix-It Squad',
      children: [
        { href: '/fix-it/request?urgency=immediate', label: 'Get Emergency Help' },
        { href: '/fix-it', label: 'Browse Fix-It Squad Pros' },
        { href: '/create-profile/fix-it-squad', label: 'Join Fix-It Squad' },
      ]
    },
        { href: '/craving', label: 'Craving' },
    { href: '/retail', label: 'Shopaholic' },
    { href: '/admin', label: 'Admin', adminOnly: true },
          { href: '/for-business-owners', label: 'For Business Owners' },
      {
        label: 'Extras',
        children: [
          { href: '/resources', label: 'All Resources' },
          { href: '/faq', label: 'FAQ' },
          { href: '/feedback', label: 'Feedback' },
          { href: '/contact', label: 'Contact' },
        ]
    },
];

export function AppHeader({ children }: { children?: React.ReactNode }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isLoading } = useUser();
  const pathname = usePathname();
  // The dashboard's active panel lives in ?tab= (the page owns it); the
  // Calendar sheet link should reflect that without useSearchParams — no
  // Suspense-boundary requirement for a header rendered on every route.
  const [dashTab, setDashTab] = useState('');
  useEffect(() => {
    const read = () => setDashTab(new URLSearchParams(window.location.search).get('tab') || '');
    read();
    window.addEventListener('popstate', read);
    window.addEventListener('hashchange', read);
    return () => {
      window.removeEventListener('popstate', read);
      window.removeEventListener('hashchange', read);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const isAdmin = user?.roles?.some((role) => role.role === 'Administrator') ?? false;

  // Updated function to render navigation links, handling sub-menus and adminOnly flag
  const renderNavLinks = (isMobile = false) => {
    const linksToRender = user ? authenticatedNavLinks : publicNavLinks;
    const closeMenu = isMobile ? { onClick: () => setIsMobileMenuOpen(false) } : {};

    return linksToRender.map((link) => {
      // If adminOnly is true and the user is not an admin, skip rendering.
      if ((link as NavLinkItem).adminOnly && !isAdmin) {
        return null;
      }

      // If the link has children, it's a NavSubMenuItem, render a dropdown/sub-menu
      if (link.children) {
        // Mobile rendering: Simple list with a heading
                if (isMobile) {
          const isFixItMobile = link.label === 'Fix-It Squad';
          return (
            <React.Fragment key={link.label}>
              <div className={`px-4 py-2 text-sm font-semibold ${isFixItMobile ? 'text-[#e11d48]' : 'text-muted-foreground'}`}>{link.label}</div>
              {link.children.map((child) => (
                <NavLink
                  key={child.href}
                  href={child.href}
                  isActive={pathname === child.href}
                  variant={link.variant}
                  {...closeMenu}
                  className={link.variant === 'brandPink' ? undefined : 'text-foreground hover:text-primary'}
                >
                  {child.label}
                </NavLink>
              ))}
            </React.Fragment>
          );
        }

                // Desktop rendering: Dropdown menu
        const isFixIt = link.label === 'Fix-It Squad';
        return (
          <DropdownMenu key={link.label}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`flex items-center text-sm font-medium hover:bg-transparent ${
                  isFixIt
                    ? '!text-[#e11d48] hover:!text-[#be123c] font-semibold'
                    : 'text-foreground hover:text-primary'
                }`}
              >
                {link.label}
                <ChevronDown className="relative top-px ml-1 h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuGroup>
                {link.children.map((child) => (
                  <DropdownMenuItem key={child.href} asChild>
                    <Link href={child.href}>{child.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }

      // Otherwise, it's a NavLinkItem, render a simple NavLink
      return (
        <NavLink
          key={link.href}
          href={link.href}
          isActive={pathname === link.href}
          variant={(link as NavLinkItem).variant}
          {...closeMenu}
                    className={
            link.href === '/stays-discover'
              ? 'font-bold !text-[#ED9249] hover:!text-[#ED9249]'
                            : link.href === '/craving'
                              ? 'font-bold !text-[#c084fc] hover:!text-[#a855f7]'
                              : link.href === '/retail'
                              ? 'font-bold !text-[#ff69b4] hover:!text-[#ff1493]'
                              : (link as NavLinkItem).variant === 'brandPink'
                  ? undefined
                  : 'text-foreground hover:text-primary'
          }
        >
          {link.label}
        </NavLink>
      );
    });
  };
  
  const getAvatarFallback = (username?: string) => {
      if (!username) return 'U';
      const names = username.split(' ');
      if (names.length > 1) {
          return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return username.substring(0, 2).toUpperCase();
  }

  return (
    <header className="bg-white sticky top-0 z-40 w-full border-b border-border/40">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center">
          {children || (
            <Link href="/">
              <Image
                src="/zamzi-logo.svg"
                alt="Zamzi Connect Logo"
                width={1097}
                height={431}
                className="h-10 w-auto"
                priority
              />
            </Link>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-4 rtl:space-x-reverse">
            {renderNavLinks()}
        </nav>

        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          {isLoading ? (
            <div className="flex items-center justify-center h-8 w-8">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
          ) : user ? (
            // User is logged in - Dropdown Menu
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.profilePhotoUrl || ""} alt={user?.username || "User"} />
                    <AvatarFallback>{getAvatarFallback(user?.username)}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.username || "User"}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user?.email || "No email"}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/dashboard/bookings"><LayoutDashboard className="mr-2 h-4 w-4" /> Bookings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/settings"><Settings className="mr-2 h-4 w-4" /> Account Settings</Link>
                </DropdownMenuItem>
                {/* This is kept for the user dropdown, which is a good UX practice */}
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link href="/admin"><Shield className="mr-2 h-4 w-4" /> Admin</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer" asChild>
                  <LogoutButton />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            // User is not logged in - show the signup CTA alongside Login.
            // The header previously offered Login only, so the primary
            // conversion action was reachable from the homepage and nowhere else.
            <div className="hidden lg:flex items-center space-x-2">
              <Button
                asChild
                size="sm"
                className="font-semibold text-white"
                style={{ backgroundColor: '#001489' }}
              >
                <Link href="/for-business-owners">Get Started</Link>
              </Button>
              <Button asChild variant="ghost" className="text-foreground hover:bg-transparent hover:text-primary">
                <Link href="/auth/login">Login</Link>
              </Button>
            </div>
          )}

          {/* Mobile Menu Toggle */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-transparent hover:text-primary">
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l-0 bg-white text-foreground">
              <SheetHeader className="sr-only">
                <SheetTitle>Mobile Menu</SheetTitle>
                <SheetDescription>Navigation links for mobile view.</SheetDescription>
              </SheetHeader>
              <nav className="flex flex-col space-y-2 pt-6">
                {user && (
                  <>
                    <div className="px-4 py-2 text-sm font-semibold text-muted-foreground">My dashboard</div>
                <NavLink href="/dashboard" isActive={pathname === '/dashboard' && dashTab !== 'calendar'} onClick={() => setIsMobileMenuOpen(false)} className="font-semibold text-foreground hover:text-primary">Dashboard home</NavLink>
                    {/* Stay's navigation truth (2026-11-03): Bookings and
                        Calendar are two rooms now — the "Bookings & calendar"
                        mash-up name is dead. Other verticals land on the
                        booking tools page the same as before. */}
                    <NavLink href="/dashboard/bookings" isActive={pathname === '/dashboard/bookings'} onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:text-primary">Bookings</NavLink>
                    <NavLink href="/dashboard?tab=calendar" isActive={pathname === '/dashboard' && dashTab === 'calendar'} onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:text-primary">Calendar</NavLink>
                    <DropdownMenuSeparator />
                  </>
                )}
                {renderNavLinks(true)}
                <DropdownMenuSeparator />
                {user ? (
                  <>
                    <NavLink href="/settings" isActive={pathname === '/settings'} onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:text-primary">Account Settings</NavLink>
                    <div onClick={() => setIsMobileMenuOpen(false)}>
                      <LogoutButton />
                    </div>
                  </>
                ) : (
                  <>
                    <NavLink href="/auth/login" isActive={pathname === '/auth/login'} onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:text-primary">Login</NavLink>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
